/**
 * Word lists used for detection.
 *
 * All entries are lowercase; matching is case-insensitive and whole-word via
 * `findPhrases` in `../text.ts`. Multi-word phrases are fine — whitespace inside
 * a phrase matches flexibly, so `'responsible for'` still matches across a line
 * break.
 *
 * Adding to these lists is a good first contribution. Two rules of thumb:
 *  1. Only add a term if it is *reliably* a problem. A false positive on
 *     someone's real job title is worse than a missed buzzword.
 *  2. Prefer the base form and add inflections explicitly where they matter.
 */

/**
 * Words that signal without evidencing. They occupy the space a concrete claim
 * should be in, and recruiters have learned to skip past them.
 *
 * Deliberately excluded: 'expert', 'specialist', 'lead', 'senior', 'architect'
 * — these are real job titles far more often than they are puffery.
 */
export const BUZZWORDS: readonly string[] = [
  'ninja',
  'guru',
  'rockstar',
  'wizard',
  'jedi',
  'maverick',
  'unicorn',
  'evangelist',
  'thought leader',
  'influencer',
  'synergy',
  'synergies',
  'go-getter',
  'self-starter',
  'hard-working',
  'hardworking',
  'hard worker',
  'passionate',
  'motivated',
  'highly motivated',
  'results-driven',
  'results-oriented',
  'detail-oriented',
  'team player',
  'dynamic',
  'proactive',
  'multitasker',
  'multi-tasker',
  'out-of-the-box',
  'outside the box',
  'think outside the box',
  'best of breed',
  'value add',
  'value-add',
  'seasoned',
  'dedicated professional',
  'proven track record',
  'wearing many hats',
  'wear many hats',
  'aspiring',
  'enthusiast',
  'tech enthusiast',
  'coding enthusiast',
  'quick learner',
  'fast learner',
  'eager to learn',
  'looking for opportunities',
  'seeking opportunities',
  'open to opportunities',
];

/**
 * Openers that describe duties instead of outcomes. These are the single most
 * common weakness in student and early-career bullet points: they say what the
 * person was assigned, not what changed because they were there.
 */
export const WEAK_OPENERS: readonly string[] = [
  'responsible for',
  'was responsible for',
  'duties included',
  'duties involved',
  'tasked with',
  'helped with',
  'helped to',
  'assisted with',
  'assisted in',
  'worked on',
  'worked with',
  'involved in',
  'participated in',
  'took part in',
  'in charge of',
  'handled',
  'dealt with',
  'exposure to',
  'familiar with',
  'learned about',
  'gained experience',
  'part of a team',
  'member of a team',
  'various tasks',
  'other duties',
];

/**
 * Filler that adds length without adding information. Cutting these is the
 * cheapest way to fit more substance inside LinkedIn's character limits.
 */
export const FILLER_PHRASES: readonly string[] = [
  'in order to',
  'due to the fact that',
  'at the end of the day',
  'needless to say',
  'it should be noted that',
  'as a matter of fact',
  'for all intents and purposes',
  'basically',
  'literally',
  'very unique',
  'really good',
  'a lot of',
  'lots of',
  'kind of',
  'sort of',
];

/**
 * Strong bullet openers. A bullet starting with one of these is almost always
 * making a claim about impact rather than listing a duty.
 *
 * Stored as base forms *and* past tense because bullets use both: LinkedIn
 * convention is past tense for previous roles, present for current ones.
 */
export const ACTION_VERBS: readonly string[] = [
  // Built / created
  'built',
  'build',
  'created',
  'create',
  'designed',
  'design',
  'developed',
  'develop',
  'engineered',
  'engineer',
  'architected',
  'implemented',
  'implement',
  'prototyped',
  'launched',
  'launch',
  'shipped',
  'ship',
  'deployed',
  'deploy',
  'authored',
  'wrote',
  'produced',
  'assembled',
  'established',
  'founded',
  'introduced',
  'pioneered',
  // Improved
  'improved',
  'improve',
  'increased',
  'increase',
  'reduced',
  'reduce',
  'optimised',
  'optimized',
  'optimise',
  'optimize',
  'accelerated',
  'streamlined',
  'automated',
  'automate',
  'refactored',
  'simplified',
  'strengthened',
  'upgraded',
  'modernised',
  'modernized',
  'doubled',
  'tripled',
  'halved',
  'eliminated',
  'cut',
  'saved',
  'boosted',
  'grew',
  'scaled',
  'expanded',
  'enhanced',
  // Led / owned
  'led',
  'lead',
  'managed',
  'manage',
  'directed',
  'coordinated',
  'coordinate',
  'owned',
  'own',
  'drove',
  'drive',
  'oversaw',
  'supervised',
  'mentored',
  'mentor',
  'coached',
  'trained',
  'train',
  'organised',
  'organized',
  'chaired',
  'headed',
  'spearheaded',
  'championed',
  'facilitated',
  'guided',
  // Analysed
  'analysed',
  'analyzed',
  'analyse',
  'analyze',
  'researched',
  'research',
  'investigated',
  'evaluated',
  'evaluate',
  'assessed',
  'audited',
  'diagnosed',
  'identified',
  'measured',
  'modelled',
  'modeled',
  'forecast',
  'forecasted',
  'benchmarked',
  'validated',
  'tested',
  'test',
  'debugged',
  'resolved',
  'fixed',
  'troubleshot',
  'quantified',
  // Delivered / collaborated
  'delivered',
  'deliver',
  'presented',
  'present',
  'negotiated',
  'negotiate',
  'partnered',
  'collaborated',
  'collaborate',
  'advised',
  'consulted',
  'supported',
  'secured',
  'won',
  'closed',
  'sold',
  'recruited',
  'onboarded',
  'published',
  'documented',
  'migrated',
  'integrated',
  'integrate',
  'configured',
  'maintained',
  'monitored',
  'standardised',
  'standardized',
];

/**
 * Signals that the reader is being invited to do something next. An About
 * section without one of these leaves interested readers with no next step.
 */
export const CTA_SIGNALS: readonly string[] = [
  'reach out',
  'get in touch',
  'contact me',
  'connect with me',
  "let's connect",
  'lets connect',
  "let's talk",
  'lets talk',
  "let's chat",
  'message me',
  'dm me',
  'drop me',
  'feel free to',
  'happy to chat',
  'happy to connect',
  'open to chat',
  'email me',
  'available at',
  'you can find me',
  'portfolio',
  'my work at',
  'reachable at',
  'hiring for',
  'apply here',
  'apply at',
  'see my',
  'check out my',
];

/**
 * Third-person self-reference. Writing your own About in the third person reads
 * as a press release and is one of the most-cited LinkedIn mistakes.
 * Matched against the opening of the text only, to reduce false positives from
 * legitimately describing other people.
 */
export const THIRD_PERSON_SIGNALS: readonly string[] = [
  'he is',
  'she is',
  'he has',
  'she has',
  'his career',
  'her career',
  'his experience',
  'her experience',
  'his background',
  'her background',
  'his work',
  'her work',
];

/** First-person markers — the voice an About section should be written in. */
export const FIRST_PERSON_SIGNALS: readonly string[] = [
  'i',
  "i'm",
  "i've",
  "i'd",
  'my',
  'me',
  'mine',
  'myself',
];

/**
 * Openers that waste the most valuable line of the profile. The first ~265
 * characters of About are all that show before "…see more", so a sentence spent
 * announcing that this is a profile is a sentence spent saying nothing.
 */
export const ABOUT_WEAK_HOOKS: readonly string[] = [
  'welcome to my profile',
  'welcome to my linkedin',
  'thanks for visiting',
  'thank you for visiting',
  'my name is',
  'hi, my name',
  'hello, my name',
  'i am a student',
  "i'm a student",
  'i am currently pursuing',
  'i am currently studying',
  'i am a hard working',
  'i am a passionate',
  "i'm a passionate",
  'to whom it may concern',
  'objective:',
  'career objective',
  'seeking a challenging',
  'looking for a challenging',
  'a challenging position',
  'about me:',
];
